/**
 * Visit store — backed by the Odoo `field.visit` model via REST API.
 *
 * Interface kept identical to the previous localStorage version, so calling
 * code doesn't change:
 *
 *   await Visit.list('-visit_date', 100)
 *   await Visit.filter({ customer_id: 7 }, '-visit_date', 50)
 *   await Visit.get(id)
 *   await Visit.create({ customer_id, visit_date, ... })
 *   await Visit.update(id, { ... })
 *   await Visit.delete(id)
 *
 * Internally everything talks to /dashboard/api/visits via the shared api
 * client (which already handles auth, base URL, and JSON envelopes).
 *
 * Collection records are still kept in localStorage — Odoo's
 * account.payment is the source of truth for actual payments; the
 * Collection store here is just a local cache/audit log.
 */
import { api } from './apiClient';

// ── Helpers ──────────────────────────────────────────────────────────────

/**
 * Translate frontend filter keys to backend query params.
 * Frontend talks "customer_id / status / salesman_email" — backend wants
 * "partner_id / state / user_id". We translate so calling code is unchanged.
 */
function translateFilterToParams(filterObj = {}, order = '-visit_date', limit = 100) {
  const params = { limit };
  for (const [key, val] of Object.entries(filterObj)) {
    if (val === undefined || val === null || val === '') continue;
    if (typeof val === 'object' && !Array.isArray(val)) {
      // operators like { $gte: '2024-01-01' }
      if (val.$gte) {
        if (key === 'visit_date' || key === 'created_date') params.date_from = val.$gte;
      }
      if (val.$lte) {
        if (key === 'visit_date' || key === 'created_date') params.date_to = val.$lte;
      }
      continue;
    }
    // Map known keys
    if (key === 'customer_id') params.partner_id = val;
    else if (key === 'status') params.state = val;
    else if (key === 'salesman_email') {
      // No direct lookup by email — skipped; backend already filters by
      // current user when not admin.
    } else {
      params[key] = val;
    }
  }
  return params;
}

/**
 * Normalize a Visit dict from the backend so calling code can keep using
 * the same property names it always did.
 */
function normalize(row) {
  if (!row) return row;
  return {
    ...row,
    // Backwards-compat aliases the old localStorage shape used
    customer_id: row.customer_id ?? (Array.isArray(row.partner_id) ? row.partner_id[0] : null),
    customer_name: row.customer_name ?? (Array.isArray(row.partner_id) ? row.partner_id[1] : ''),
    salesman_email: row.salesman_email ?? null,
    salesman_name: row.salesman_name ?? null,
    status: row.state, // 'active', 'completed', 'planned', ...
    // Common GPS aliases
    latitude: row.latitude ?? row.checkin_latitude ?? null,
    longitude: row.longitude ?? row.checkin_longitude ?? null,
    gps_accuracy_meters: row.gps_accuracy_meters ?? row.checkin_accuracy ?? null,
  };
}

// Local "draft" buffer for visits that fail to sync (offline / GPS denied / etc.)
const PENDING_KEY = 'pending_visits';
function pushPending(payload) {
  try {
    const list = JSON.parse(localStorage.getItem(PENDING_KEY) || '[]');
    list.push({ ...payload, _local_id: `local_${Date.now()}`, _ts: new Date().toISOString() });
    localStorage.setItem(PENDING_KEY, JSON.stringify(list));
  } catch {
    // best effort
  }
}
function localFallbackList() {
  try {
    return JSON.parse(localStorage.getItem(PENDING_KEY) || '[]');
  } catch {
    return [];
  }
}

// ── Visit API ────────────────────────────────────────────────────────────

export const Visit = {
  /** List visits, newest first by default. */
  async list(order = '-visit_date', limit = 100) {
    try {
      const res = await api.get('/visits', { limit });
      const rows = res?.success ? (res.data || []) : [];
      return rows.map(normalize);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[visits] list failed, returning local fallback:', e?.message);
      return localFallbackList().map(normalize);
    }
  },

  /** Filter visits — supports customer_id, status, date ranges. */
  async filter(filterObj = {}, order = '-visit_date', limit = 100) {
    try {
      // If the caller asked for a specific id, short-circuit to GET /visits/<id>
      if (filterObj.id && typeof filterObj.id !== 'object') {
        const one = await Visit.get(filterObj.id);
        return one ? [one] : [];
      }

      const params = translateFilterToParams(filterObj, order, limit);
      const res = await api.get('/visits', params);
      const rows = res?.success ? (res.data || []) : [];
      let normalized = rows.map(normalize);

      // Apply any client-side filters the backend doesn't support
      if (filterObj.salesman_email) {
        normalized = normalized.filter((r) => r.salesman_email === filterObj.salesman_email);
      }
      return normalized;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn('[visits] filter failed:', e?.message);
      return [];
    }
  },

  /** Get one visit by id (numeric — Odoo id). */
  async get(id) {
    if (!id) return null;
    try {
      const res = await api.get(`/visits/${parseInt(id, 10)}`);
      return res?.success ? normalize(res.data) : null;
    } catch {
      return null;
    }
  },

  /**
   * Create a visit. The dashboard usually passes:
   *   { customer_id, customer_name, salesman_email, salesman_name,
   *     visit_date, check_in_time, status: 'active',
   *     latitude, longitude, gps_verified, gps_accuracy_meters }
   *
   * We translate to the backend's shape and call POST /visits with the
   * `check_in: true` flag so the visit immediately moves to 'active' and
   * records GPS coords in one round-trip.
   */
  async create(data) {
    const payload = {
      partner_id: data.customer_id ?? data.partner_id,
      visit_date: data.visit_date ?? data.check_in_time ?? new Date().toISOString(),
      notes: data.notes ?? '',
      location_address: data.location_address ?? '',
    };
    // If GPS was provided AND the caller is starting an active visit,
    // ask the backend to check in immediately.
    const startingActive = data.status === 'active' || data.check_in_time;
    if (startingActive && data.latitude && data.longitude) {
      payload.check_in = true;
      payload.latitude = data.latitude;
      payload.longitude = data.longitude;
      payload.accuracy = data.gps_accuracy_meters ?? null;
    }
    try {
      const res = await api.post('/visits', payload);
      if (!res?.success) {
        pushPending(data);
        throw new Error(res?.error || 'Failed to create visit');
      }
      const created = res.data;

      // If the caller wants the visit active but we couldn't check-in
      // inline (no GPS), do an explicit check-in NOW with whatever we have.
      // Errors here are not silent — we surface them so the UI doesn't
      // think the visit is active when it's actually 'planned'.
      if (startingActive && created.state !== 'active') {
        const ci = await api.post(`/visits/${created.id}/check-in`, {
          latitude: data.latitude ?? null,
          longitude: data.longitude ?? null,
          accuracy: data.gps_accuracy_meters ?? null,
          location_address: data.location_address ?? null,
        });
        if (ci?.success) return normalize(ci.data);
        // If the explicit check-in failed, surface the actual error
        throw new Error(ci?.error || 'Visit created but check-in failed');
      }
      return normalize(created);
    } catch (e) {
      pushPending(data);
      throw e;
    }
  },

  /**
   * Update a visit. Smart routing:
   *   - if `status: 'completed'` and check_out_time was added → call /check-out
   *     (auto-checks-in first if the visit is still 'planned')
   *   - if `status: 'cancelled'` → call /cancel
   *   - otherwise → PUT /visits/<id>
   */
  async update(id, data) {
    const numId = parseInt(id, 10);
    if (!numId) throw new Error(`Invalid visit id: ${id}`);

    // Check-out path
    if (data.status === 'completed' || data.check_out_time) {
      // First make sure the visit is actually active. If it's still
      // 'planned' (because the initial check-in failed), do a quick
      // check-in now so the check-out can proceed.
      try {
        const current = await api.get(`/visits/${numId}`);
        if (current?.success && current.data?.state === 'planned') {
          await api.post(`/visits/${numId}/check-in`, {
            latitude: data.checkout_latitude ?? null,
            longitude: data.checkout_longitude ?? null,
          });
        }
      } catch {
        // If we can't check the state, try the check-out anyway —
        // the backend will tell us if something's wrong.
      }

      const payload = {
        latitude: data.checkout_latitude ?? null,
        longitude: data.checkout_longitude ?? null,
        outcome: data.outcome ?? null,
        no_sale_reason: data.no_sale_reason ?? null,
        notes: data.notes ?? null,
      };
      const res = await api.post(`/visits/${numId}/check-out`, payload);
      if (res?.success) return normalize(res.data);
      throw new Error(res?.error || 'Check-out failed');
    }

    // Cancel path
    if (data.status === 'cancelled') {
      const res = await api.post(`/visits/${numId}/cancel`, {
        reason: data.cancel_reason ?? data.notes ?? '',
      });
      if (res?.success) return normalize(res.data);
      throw new Error(res?.error || 'Cancel failed');
    }

    // Plain update
    const allowed = {};
    if (data.visit_date) allowed.visit_date = data.visit_date;
    if (data.notes !== undefined) allowed.notes = data.notes;
    if (data.outcome) allowed.outcome = data.outcome;
    if (data.no_sale_reason !== undefined) allowed.no_sale_reason = data.no_sale_reason;
    const res = await api.put(`/visits/${numId}`, allowed);
    if (res?.success) return normalize(res.data);
    throw new Error(res?.error || 'Update failed');
  },

  async delete(id) {
    const numId = parseInt(id, 10);
    if (!numId) return { deleted: false };
    const res = await api.del(`/visits/${numId}`);
    return res?.success ? { deleted: true } : { deleted: false, error: res?.error };
  },

  /** Stats for dashboards. */
  async stats(params = {}) {
    const res = await api.get('/visits/stats', params);
    return res?.success ? res.data : {};
  },

  async clear() {
    localStorage.removeItem(PENDING_KEY);
  },
};

// ── Collection store ─────────────────────────────────────────────────────

const LS_COLLECTIONS = 'dashboard_collections_v1';

function loadCollections() {
  try {
    const raw = localStorage.getItem(LS_COLLECTIONS);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}
function saveCollections(list) {
  try {
    localStorage.setItem(LS_COLLECTIONS, JSON.stringify(list));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[collections] save failed:', e);
  }
}
function newLocalId() {
  return `c_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
}
function applyOrder(list, order) {
  if (!order) return list;
  const desc = order.startsWith('-');
  const field = desc ? order.slice(1) : order;
  return list.slice().sort((a, b) => {
    const av = a[field];
    const bv = b[field];
    if (av === bv) return 0;
    if (av === undefined || av === null) return 1;
    if (bv === undefined || bv === null) return -1;
    return desc ? (av < bv ? 1 : -1) : av < bv ? -1 : 1;
  });
}
function applyFilter(list, filterObj) {
  if (!filterObj || typeof filterObj !== 'object') return list.slice();
  return list.filter((row) =>
    Object.entries(filterObj).every(([k, v]) => {
      if (v && typeof v === 'object' && !Array.isArray(v)) {
        if (v.$gte !== undefined && !(row[k] >= v.$gte)) return false;
        if (v.$lte !== undefined && !(row[k] <= v.$lte)) return false;
        return true;
      }
      return row[k] === v;
    })
  );
}

export const Collection = {
  async list(order = '-created_date', limit = 100) {
    let rows = loadCollections();
    rows = applyOrder(rows, order);
    return rows.slice(0, limit);
  },
  async filter(filterObj = {}, order = '-created_date', limit = 100) {
    let rows = loadCollections();
    rows = applyFilter(rows, filterObj);
    rows = applyOrder(rows, order);
    return rows.slice(0, limit);
  },
  async create(data) {
    const rows = loadCollections();
    const now = new Date().toISOString();
    const row = { id: newLocalId(), created_date: now, updated_date: now, ...data };
    rows.push(row);
    saveCollections(rows);
    return row;
  },
  async update(id, data) {
    const rows = loadCollections();
    const idx = rows.findIndex((r) => r.id === id);
    if (idx < 0) throw new Error(`Collection ${id} not found`);
    rows[idx] = { ...rows[idx], ...data, updated_date: new Date().toISOString() };
    saveCollections(rows);
    return rows[idx];
  },
  async delete(id) {
    saveCollections(loadCollections().filter((r) => r.id !== id));
    return { deleted: true };
  },
};

export const entities = { Visit, Collection };
